import { MB_ROLES, strings } from '@constants'
import {
	getHighestEnquiryWeight,
	getHighestMarginAndPercentage,
	getHighestOrderWeight,
	getHighestRevenue,
	getTotalOverdue,
	getHighestDispatch,
	getHighestSupplierQuotes,
	getHighestQuoteConversion,
	getHighestOrderGivenQuotes,
	getHighestUniqueSupplierQuoted,
	getHighestNewSupplierOnboarded,
} from '@data'
import { displayAmount } from '@utils'

const formatQuantities = (quantities) => {
	const formatted = Array.isArray(quantities)
		? quantities
				.map((q) => `${q?.value ?? ''} ${q?.unit ?? ''}`.trim())
				.filter(Boolean)
				.join(', ')
		: ''
	return formatted || '-'
}

const CONFIG = {
	[MB_ROLES.SALES]: {
		cards: [
			{
				dataKey: 'revenue',
				title: 'Revenue',
				accent: 'error',
				valueLabel: 'Revenue',
				valueKey: 'revenue',
				unitKey: 'revenue_unit',
				formatValue: (item, value) => (value != null && value !== '' ? `${displayAmount(value)} ${item?.revenue_unit ?? ''}`.trim() : ''),
				showTotal: true,
				formatTotal: (total) => `${displayAmount(total / 1e7)} Cr`,
			},
			{
				dataKey: 'margin',
				responseKey: 'marginAndPercentage',
				dataPath: 'margin_leaderboard',
				title: 'Gross Margin',
				accent: 'info',
				valueLabel: 'Margin',
				valueKey: 'total_margin',
				formatValue: (item, value) => (value != null && value !== '' ? `${displayAmount(value)} ${item?.margin_unit ?? ''}`.trim() : ''),
			},
			{
				dataKey: 'gmPercentage',
				responseKey: 'marginAndPercentage',
				dataPath: 'percentage_leaderboard',
				title: 'Highest GM %',
				accent: 'info',
				valueLabel: 'GM %',
				valueKey: 'margin_percentage',
				formatValue: (_item, value) => (value != null && value !== '' ? `${value}%` : ''),
			},
			{
				dataKey: 'orderWeight',
				title: 'Order Weight',
				accent: 'success',
				valueLabel: 'Weight',
				formatValue: (item) => formatQuantities(item?.quantities),
			},
			{
				dataKey: 'enquiryWeight',
				title: 'Enquiry Weight',
				accent: 'warning',
				valueLabel: 'Weight',
				formatValue: (item) => formatQuantities(item?.quantities),
			},
			{
				dataKey: 'totalOverdue',
				title: 'Total Average Delays',
				accent: 'celeste',
				valueLabel: 'Avg Delay',
				valueKey: 'total_overdue',
				formatValue: (_, value) => (value != null ? `${value} days` : '--'),
			},
		],
		fetchList: [
			{ fetchKey: 'revenue', apiFn: getHighestRevenue },
			{ fetchKey: 'marginAndPercentage', apiFn: getHighestMarginAndPercentage },
			{ fetchKey: 'orderWeight', apiFn: getHighestOrderWeight },
			{ fetchKey: 'enquiryWeight', apiFn: getHighestEnquiryWeight },
			{ fetchKey: 'totalOverdue', apiFn: getTotalOverdue },
		],
	},
	[MB_ROLES.SUPPLY]: {
		cards: [
			{
				dataKey: 'dispatch',
				title: 'Highest Dispatch (in MT)',
				accent: 'error',
				valueLabel: strings('dispatch'),
				valueKey: 'total_weight',
				formatValue: (item, value) =>
					Array.isArray(item?.quantities) ? formatQuantities(item.quantities) : value != null ? displayAmount(value) : '',
			},
			{
				dataKey: 'supplierQuotes',
				title: 'No. of quotes given',
				accent: 'info',
				valueLabel: strings('quotes'),
				valueKey: 'total_quotes',
				formatValue: (_, value) => (value != null ? value : ''),
			},
			{
				dataKey: 'quoteConversion',
				title: 'Highest Quote conversion %',
				accent: 'info',
				valueLabel: strings('conversion'),
				valueKey: 'conversion_percentage',
				formatValue: (_, value) => (value != null && value !== '' ? `${value}%` : ''),
				extraColumns: [
					{
						label: strings('quote', 'ratio'),
						// single-arg contract (item) => string, unlike the two-arg card.formatValue(item, rawValue):
						// there's no single valueKey backing a ratio computed from two fields.
						formatValue: (item) => {
							const given = item?.order_given_quotes
							const total = item?.total_quotes
							return given != null && total != null ? `${given}/${total}` : ''
						},
					},
				],
			},
			{
				dataKey: 'orderGivenQuotes',
				title: 'Highest Order Converted (in MT)',
				accent: 'success',
				valueLabel: strings('quotes'),
				valueKey: 'total_weight',
				formatValue: (_, value) => (value != null ? value : ''),
			},
			{
				dataKey: 'uniqueSupplierQuoted',
				title: 'No. of Unique suppliers quoted from',
				accent: 'warning',
				valueLabel: strings('count'),
				valueKey: 'total_quotes',
				formatValue: (_, value) => (value != null ? value : ''),
			},
			{
				dataKey: 'newSupplierOnboarded',
				title: 'New suppliers onboarded',
				accent: 'celeste',
				valueLabel: strings('onboarded'),
				valueKey: 'supplier_onboarded',
				formatValue: (_, value) => (value != null ? value : ''),
			},
		],
		fetchList: [
			{ fetchKey: 'dispatch', apiFn: getHighestDispatch },
			{ fetchKey: 'supplierQuotes', apiFn: getHighestSupplierQuotes },
			{ fetchKey: 'quoteConversion', apiFn: getHighestQuoteConversion },
			{ fetchKey: 'orderGivenQuotes', apiFn: getHighestOrderGivenQuotes },
			{ fetchKey: 'uniqueSupplierQuoted', apiFn: getHighestUniqueSupplierQuoted },
			{ fetchKey: 'newSupplierOnboarded', apiFn: getHighestNewSupplierOnboarded },
		],
	},
}

export const getLeaderboardConfig = (type) => CONFIG[type] ?? CONFIG[MB_ROLES.SALES]

export const ALL_FETCH_KEYS = Object.values(CONFIG).flatMap((cfg) => cfg.fetchList.map((f) => f.fetchKey))
